import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const CART_SYNC_EVENT = 'theme:cart-synced';
const rootUrl = () => window.Shopify?.routes?.root || '/';
const cartJsonUrl = () => window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : `${rootUrl()}cart.js`;

let scheduledSync = null;
let activeSync = null;
let lastKnownCount = null;

function cartComponents() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'));
}

function updateCartIcons(itemCount, animate = true) {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    if (typeof icon.renderCartBubble === 'function') {
      icon.renderCartBubble(itemCount, animate);
      return;
    }

    const count = icon.querySelector('[ref="cartBubbleCount"]');
    const bubble = icon.querySelector('[ref="cartBubble"]');
    if (count) count.textContent = itemCount < 100 ? String(itemCount) : '';
    bubble?.classList.toggle('visually-hidden', itemCount === 0);
    icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
  });

  sessionStorage.setItem(
    'cart-count',
    JSON.stringify({ value: String(itemCount), timestamp: Date.now() })
  );
}

async function fetchCart() {
  const response = await fetch(cartJsonUrl(), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  const cart = await response.json();
  if (!response.ok || cart?.status) {
    throw new Error(cart?.description || cart?.message || `Unable to load cart (${response.status})`);
  }
  return cart;
}

function componentNeedsFullRender(component, cart) {
  const isDrawer = component.hasAttribute('data-drawer');
  const isEmptyMarkup = Boolean(
    component.querySelector('[data-cart-drawer-empty], .cart-drawer--empty, .cart-page--empty, .cart-items__wrapper--drawer-empty')
  );
  const shouldBeEmpty = Number(cart.item_count || 0) === 0;
  const renderedRows = component.querySelectorAll('[ref^="cartItemRow"], .cart-items__table-row').length;

  if (isEmptyMarkup !== shouldBeEmpty) return true;
  if (!shouldBeEmpty && renderedRows === 0) return true;

  const hiddenCount = component.querySelector('[ref="cartItemCount"]')?.textContent;
  if (hiddenCount != null && Number(hiddenCount) !== Number(cart.item_count || 0)) return true;

  // Drawer content contains several conditionally rendered regions. A full morph is safer
  // after discounts because hydration cannot insert a row/summary that did not exist before.
  return isDrawer;
}

async function renderCartSections(cart, force = false) {
  const tasks = [];

  for (const component of cartComponents()) {
    const sectionId = component.dataset.sectionId;
    if (!sectionId) continue;
    if (!force && !componentNeedsFullRender(component, cart)) continue;

    tasks.push(
      sectionRenderer
        .renderSection(sectionId, {
          cache: false,
          mode: 'full',
          injectStylesheet: true,
          url: new URL(window.location.href),
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn(`[cart-live-sync] Could not refresh section ${sectionId}:`, error);
          }
        })
    );
  }

  await Promise.all(tasks);
}

async function performSync({ force = false, animateCount = true } = {}) {
  if (activeSync) {
    try {
      await activeSync;
    } catch (_) {
      // The next sync below is the recovery attempt.
    }
  }

  activeSync = (async () => {
    const cart = await fetchCart();
    const countChanged = lastKnownCount !== Number(cart.item_count || 0);
    updateCartIcons(Number(cart.item_count || 0), animateCount && countChanged);
    lastKnownCount = Number(cart.item_count || 0);
    await renderCartSections(cart, force);

    document.dispatchEvent(
      new CustomEvent(CART_SYNC_EVENT, {
        detail: { cart },
        bubbles: true,
      })
    );

    return cart;
  })().finally(() => {
    activeSync = null;
  });

  return activeSync;
}

function scheduleSync(options = {}) {
  if (scheduledSync) window.clearTimeout(scheduledSync);
  scheduledSync = window.setTimeout(() => {
    scheduledSync = null;
    performSync(options).catch((error) => console.warn('[cart-live-sync] Sync failed:', error));
  }, 40);
}

function onCartLinesUpdate(event) {
  const promise = event?.promise;
  if (promise && typeof promise.then === 'function') {
    promise
      .then(() => scheduleSync({ force: true }))
      .catch((error) => {
        if (error?.name !== 'AbortError') scheduleSync({ force: true, animateCount: false });
      });
  } else {
    scheduleSync({ force: true });
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
document.addEventListener('cart:refresh', () => scheduleSync({ force: true, animateCount: false }));
document.addEventListener('cart:discount-applied', () => scheduleSync({ force: true, animateCount: false }));
document.addEventListener('shopify:section:load', () => scheduleSync({ force: false, animateCount: false }));
window.addEventListener('pageshow', () => scheduleSync({ force: false, animateCount: false }));

// Refresh immediately before an empty drawer is opened. This covers app/script additions
// that update the Shopify cart without dispatching the Horizon standard event.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[aria-controls="cart-drawer"], [data-testid="cart-drawer-trigger"]')) return;
    scheduleSync({ force: true, animateCount: false });
  },
  { capture: true }
);

window.ThemeCartSync = {
  sync: (options) => performSync({ force: true, ...(options || {}) }),
  schedule: scheduleSync,
  eventName: CART_SYNC_EVENT,
};
